import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuthStore } from "../../stores/authStore";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../services/supabase";
import { BusinessWithDistance } from "../../components/ui/cards/types";
import Papa from "papaparse";
import {
  getBusinessesByStatus,
  setBusinessStatus,
  BUSINESS_STATUS_CONFIG,
  getBusinessStatusInfo,
  BusinessStatus
} from "../../services/businessAdmin";
import type { Database } from '../../types/database';

import ReviewManagementPage from './ReviewManagementPage';
import UsersRegistrationsTab from '../../components/admin/UsersRegistrationsTab';

type BusinessesRow = Database['public']['Tables']['businesses']['Row'];
type BusinessesUpdate = Database['public']['Tables']['businesses']['Update'];
type TyreSpecRow = Database['public']['Tables']['tyre_specifications']['Row'];
type PaymentRow = { id: string; business_id: string | null; amount?: number; created_at?: string; [k: string]: any };

const PlatformAdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState("overview");
  type Application = { id: string; [key: string]: any };
  const [applications, setApplications] = useState<Application[]>([]);
  const [applicationsLoading, setApplicationsLoading] = useState(true);
  const [applicationsError, setApplicationsError] = useState<string | null>(null);
  const [newTyreFile, setNewTyreFile] = useState<File | null>(null);
  const [newVehicleFile, setNewVehicleFile] = useState<File | null>(null);
  // Analytics state
  const [analytics, setAnalytics] = useState<{
    totalUsers: number;
    totalBusinesses: number;
    monthlyRevenue: number;
    activeSearches: number;
    totalBookings: number;
    platformGrowth: number;
  }>({
    totalUsers: 0,
    totalBusinesses: 0,
    monthlyRevenue: 0,
    activeSearches: 0,
    totalBookings: 0,
    platformGrowth: 0,
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  // Monthly fee management state
  const [monthlyFees, setMonthlyFees] = useState<Record<string, number | string>>({});
  const [feeSaving, setFeeSaving] = useState<Record<string, boolean>>({});
  const [feeSuccess, setFeeSuccess] = useState<Record<string, boolean>>({});
  const [feeErrors, setFeeErrors] = useState<Record<string, string | null>>({});

  // Tyre inventory state
  type Tyre = {
    id?: string;
    brand: string;
    name: string;
    size: string;
    type?: string;
    image_url?: string;
    model?: string;
    tyre_spec_id?: string;
    quantity?: number;
  };
  const [tyreDatabase, setTyreDatabase] = useState<Tyre[]>([]);
  const [filteredTyres, setFilteredTyres] = useState<Tyre[]>([]);
  const [tyreLoading, setTyreLoading] = useState(true);
  const [tyreError, setTyreError] = useState<string | null>(null);
  
  // Filtering and search state
  const [searchTerm, setSearchTerm] = useState("");
  const [brandFilter, setBrandFilter] = useState("");
  const [sizeFilter, setSizeFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [sortBy, setSortBy] = useState<"brand" | "name" | "size" | "type">("brand");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Business management state
  const [allBusinesses, setAllBusinesses] = useState<any[]>([]);
  const [filteredBusinesses, setFilteredBusinesses] = useState<any[]>([]);
  const [businessesLoading, setBusinessesLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<BusinessStatus | 'all'>('all');
  const [businessSearchTerm, setBusinessSearchTerm] = useState('');

  // Vehicle database state
  type Vehicle = {
    id?: string;
    make_id?: string;
    make_name?: string;
    name: string;
    year_from?: number;
    year_to?: number;
    default_tyre_size?: string;
    created_at?: string;
  };
  type VehicleMake = {
    id?: string;
    name: string;
    popular?: boolean;
    created_at?: string;
  };
  const [vehicleMakes, setVehicleMakes] = useState<VehicleMake[]>([]);
  const [vehicleModels, setVehicleModels] = useState<Vehicle[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<Vehicle[]>([]);
  const [vehicleLoading, setVehicleLoading] = useState(true);
  const [vehicleError, setVehicleError] = useState<string | null>(null);
  
  // Vehicle filtering and search state
  const [vehicleSearchTerm, setVehicleSearchTerm] = useState("");
  const [makeFilter, setMakeFilter] = useState("");
  const [yearFromFilter, setYearFromFilter] = useState("");
  const [yearToFilter, setYearToFilter] = useState("");
  const [vehicleSortBy, setVehicleSortBy] = useState<"make_name" | "name" | "year_from" | "default_tyre_size">("make_name");
  const [vehicleSortOrder, setVehicleSortOrder] = useState<"asc" | "desc">("asc");
  const [vehicleItemsPerPage, setVehicleItemsPerPage] = useState(20);
  const [vehicleCurrentPage, setVehicleCurrentPage] = useState(1);

  // Vehicle upload progress state
  const [vehicleUploadProgress, setVehicleUploadProgress] = useState(0);
  const [isVehicleUploading, setIsVehicleUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [uploadResults, setUploadResults] = useState<{
    successCount: number;
    errorCount: number;
    errors: string[];
    totalRows: number;
  } | null>(null);

  // Suggestions state
  type Suggestion = {
    id: string;
    name: string;
    address: string;
    city: string;
    province: string;
    phone?: string | null;
    email?: string | null;
    additional_info?: string | null;
    suggested_by_location?: string | null;
    user_coordinates?: any;
    review_notes?: string | null;
    reviewed_at?: string | null;
    reviewed_by?: string | null;
    status?: string | null;
    created_at: string | null;
    updated_at: string | null;
  };
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState<Suggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(true);
  const [suggestionsError, setSuggestionsError] = useState<string | null>(null);

  // Suggestions filtering and search state
  const [suggestionsSearchTerm, setSuggestionsSearchTerm] = useState("");
  const [suggestionsStatusFilter, setSuggestionsStatusFilter] = useState<string>("all");
  const [suggestionsProvinceFilter, setSuggestionsProvinceFilter] = useState("");
  const [suggestionsSortBy, setSuggestionsSortBy] = useState<"created_at" | "name" | "city" | "status">("created_at");
  const [suggestionsSortOrder, setSuggestionsSortOrder] = useState<"asc" | "desc">("desc");
  const [suggestionsItemsPerPage, setSuggestionsItemsPerPage] = useState(20);
  const [suggestionsCurrentPage, setSuggestionsCurrentPage] = useState(1);

  const [recentPayments, setRecentPayments] = useState<any[]>([]);
  const [recentTyreUpdates, setRecentTyreUpdates] = useState<any[]>([]);
  const { logout, user } = useAuthStore();
  const navigate = useNavigate();

  // Dropdown state for Business Management tab
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Suggestion view modal state
  const [suggestionModalOpen, setSuggestionModalOpen] = useState(false);
  const [selectedSuggestion, setSelectedSuggestion] = useState<Suggestion | null>(null);

  // Fetch all businesses for admin view
  useEffect(() => {
    setBusinessesLoading(true);
    const fetchAllBusinesses = async () => {
      const { data, error } = await getBusinessesByStatus(); // Get all businesses
      if (error) {
        console.error("Failed to fetch businesses:", error);
        setAllBusinesses([]);
        setFilteredBusinesses([]);
      } else if (Array.isArray(data)) {
        setAllBusinesses(data);
        setFilteredBusinesses(data);
      } else {
        setAllBusinesses([]);
        setFilteredBusinesses([]);
      }
      setBusinessesLoading(false);
    };
  
    // Fetch recent payments
    const fetchRecentPayments = async () => {
      const { data } = await supabase
        .from("payments")
        .select("id, business_id, created_at")
        .order("created_at", { ascending: false })
        .limit(3);
      // Optionally join with businesses for business_name
      if (Array.isArray(data)) {
        const paymentsTyped = (data as PaymentRow[]);
        const paymentsWithNames = await Promise.all(
          paymentsTyped.map(async (pay: PaymentRow) => {
            if (pay.business_id) {
              const { data: biz } = await supabase
                .from("businesses")
                .select("name")
                .eq("id", pay.business_id)
                .maybeSingle();
              const businessName = (biz as { name: string } | null)?.name || "";
              return { ...pay, business_name: businessName };
            }
            return pay;
          })
        );
        setRecentPayments(paymentsWithNames);
      } else {
        setRecentPayments([]);
      }
    };
  
    // Fetch recent tyre updates
    const fetchRecentTyreUpdates = async () => {
      const { data } = await supabase
        .from("tyre_specifications")
        .select("id, created_at")
        .order("created_at", { ascending: false })
        .limit(3);
      // Group by created_at date for summary
      if (Array.isArray(data)) {
        const recent = (data as TyreSpecRow[]).map(t => ({ ...t, count: 1 }));
        setRecentTyreUpdates(recent);
      } else {
        setRecentTyreUpdates([]);
      }
    };


  
    fetchAllBusinesses();
    fetchRecentPayments();
    fetchRecentTyreUpdates();
  }, []);

  // Filter businesses based on status and search term
  useEffect(() => {
    let filtered = [...allBusinesses];

    // Apply status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(business => business.business_status === statusFilter);
    }

    // Apply search filter
    if (businessSearchTerm) {
      filtered = filtered.filter(business =>
        business.name?.toLowerCase().includes(businessSearchTerm.toLowerCase()) ||
        business.contact_email?.toLowerCase().includes(businessSearchTerm.toLowerCase()) ||
        business.description?.toLowerCase().includes(businessSearchTerm.toLowerCase())
      );
    }

    setFilteredBusinesses(filtered);
  }, [allBusinesses, statusFilter, businessSearchTerm]);



  useEffect(() => {
    // Fetch business applications
    const fetchApplications = async () => {
      setApplicationsLoading(true);
      setApplicationsError(null);
      const { data, error } = await supabase
        .from("businesses")
        .select("*")
        .eq("business_status", "pending")
        .order("created_at", { ascending: false });
      if (error) {
        setApplicationsError(error.message);
        setApplications([]);
      } else {
        setApplications(data || []);
      }
      setApplicationsLoading(false);
    };

    // Fetch analytics
    const fetchAnalytics = async () => {
      setAnalyticsLoading(true);
      try {
        // Total users
        const { count: userCount } = await supabase
          .from("user_profiles")
          .select("*", { count: "exact", head: true });
        // Total businesses
        const { count: businessCount } = await supabase
          .from("businesses")
          .select("*", { count: "exact", head: true });
        
        // Monthly revenue (sum of actual monthly fees)
        const { data: businessFees } = await supabase
          .from("businesses")
          .select("monthly_fee")
          .eq("business_status", "active");

        const businessFeesTyped = (businessFees as BusinessesRow[]);
        const totalMonthlyRevenue = businessFeesTyped?.reduce((sum, business) => {
          return sum + (business.monthly_fee || 0);
        }, 0) || 0;
        // Bookings (if table exists)
        let totalBookings = 0;
        try {
          const { count: bookingsCount } = await supabase
            .from("bookings")
            .select("*", { count: "exact", head: true });
          totalBookings = bookingsCount || 0;
        } catch {
          totalBookings = 0;
        }

        // Active searches (last 24h)
        let activeSearches = 0;
        try {
          const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
          const { count: searchCount } = await supabase
            .from("search_activity")
            .select("*", { count: "exact", head: true })
            .gte("created_at", since);
          activeSearches = searchCount || 0;
        } catch {
          activeSearches = 0;
        }

        // Platform growth: % increase in users over last month
        let platformGrowth = 0;
        try {
          const now = new Date();
          const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          const lastMonthISO = lastMonth.toISOString();
          const { count: prevUserCount } = await supabase
            .from("user_profiles")
            .select("*", { count: "exact", head: true })
            .lt("created_at", lastMonthISO);
          if (prevUserCount && prevUserCount > 0) {
            platformGrowth = Math.round(
              ((Number(userCount || 0) - prevUserCount) / prevUserCount) * 100
            );
          } else {
            platformGrowth = 0;
          }
        } catch {
          platformGrowth = 0;
        }

        setAnalytics({
          totalUsers: userCount || 0,
          totalBusinesses: businessCount || 0,
          monthlyRevenue: totalMonthlyRevenue,
          activeSearches,
          totalBookings,
          platformGrowth,
        });
      } catch (err) {
        console.error(
          err instanceof Error
            ? err.message
            : "Failed to load analytics"
        );
      }
      setAnalyticsLoading(false);
    };
// Fetch historical analytics (new users, bookings, revenue per month)
/* Removed unused fetchHistoricalAnalytics function */

    // Fetch tyre inventory
    const fetchTyres = async () => {
      setTyreLoading(true);
      setTyreError(null);
      const { data, error } = await supabase
        .from("tyre_specifications")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) {
        setTyreError(error.message);
        setTyreDatabase([]);
        setFilteredTyres([]);
      } else {
        const tyres = (data || []).map(tyre => ({
          id: tyre.id,
          brand: tyre.brand,
          name: tyre.name,
          size: tyre.size,
          type: tyre.type ?? undefined,
          image_url: tyre.image_url ?? undefined,
          model: tyre.model ?? undefined
        }));
        setTyreDatabase(tyres);
        setFilteredTyres(tyres);
      }
      setTyreLoading(false);
    };

    // Fetch vehicle data
    const fetchVehicles = async () => {
      setVehicleLoading(true);
      setVehicleError(null);
      
      try {
        // Fetch vehicle makes
        const { data: makesData, error: makesError } = await supabase
          .from("vehicle_makes")
          .select("*")
          .order("name", { ascending: true });

        if (makesError) {
          setVehicleError(makesError.message);
          setVehicleMakes([]);
          setVehicleModels([]);
          setFilteredVehicles([]);
          setVehicleLoading(false);
          return;
        }

        setVehicleMakes((makesData || []).map(make => ({
          ...make,
          popular: make.popular ?? undefined,
          created_at: make.created_at ?? undefined
        })));

        // Fetch vehicle models with make information
        const { data: modelsData, error: modelsError } = await supabase
          .from("vehicle_models")
          .select(`
            *,
            vehicle_makes!inner(name)
          `)
          .order("created_at", { ascending: false });

        if (modelsError) {
          setVehicleError(modelsError.message);
          setVehicleModels([]);
          setFilteredVehicles([]);
        } else {
          const vehicles = (modelsData || []).map((model: any) => ({
            ...model,
            make_name: model.vehicle_makes?.name || 'Unknown'
          }));
          setVehicleModels(vehicles);
          setFilteredVehicles(vehicles);
        }
      } catch (err) {
        setVehicleError(err instanceof Error ? err.message : "Failed to load vehicle data");
        setVehicleMakes([]);
        setVehicleModels([]);
        setFilteredVehicles([]);
      }
      
      setVehicleLoading(false);
    };

    // Fetch suggestions
    const fetchSuggestions = async () => {
      setSuggestionsLoading(true);
      setSuggestionsError(null);
      const { data, error } = await supabase
        .from("fitment_centre_suggestions")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) {
        setSuggestionsError(error.message);
        setSuggestions([]);
        setFilteredSuggestions([]);
      } else {
        setSuggestions(data || []);
        setFilteredSuggestions(data || []);
      }
      setSuggestionsLoading(false);
    };

    fetchApplications();
    fetchAnalytics();
    fetchTyres();
    fetchVehicles();
    fetchSuggestions();
  }, []);

  // Filter and search effect for tyres
  useEffect(() => {
    let filtered = [...tyreDatabase];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(tyre =>
        tyre.brand?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tyre.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tyre.size?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tyre.type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply brand filter
    if (brandFilter) {
      filtered = filtered.filter(tyre => tyre.brand === brandFilter);
    }

    // Apply size filter
    if (sizeFilter) {
      filtered = filtered.filter(tyre => tyre.size === sizeFilter);
    }

    // Apply type filter
    if (typeFilter) {
      filtered = filtered.filter(tyre => tyre.type === typeFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = a[sortBy] || "";
      const bValue = b[sortBy] || "";
      const comparison = aValue.localeCompare(bValue);
      return sortOrder === "asc" ? comparison : -comparison;
    });

    setFilteredTyres(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  }, [tyreDatabase, searchTerm, brandFilter, sizeFilter, typeFilter, sortBy, sortOrder]);

  // Filter and search effect for vehicles
  useEffect(() => {
    let filtered = [...vehicleModels];

    // Apply search filter
    if (vehicleSearchTerm) {
      filtered = filtered.filter(vehicle =>
        vehicle.make_name?.toLowerCase().includes(vehicleSearchTerm.toLowerCase()) ||
        vehicle.name?.toLowerCase().includes(vehicleSearchTerm.toLowerCase()) ||
        vehicle.default_tyre_size?.toLowerCase().includes(vehicleSearchTerm.toLowerCase())
      );
    }

    // Apply make filter
    if (makeFilter) {
      filtered = filtered.filter(vehicle => vehicle.make_name === makeFilter);
    }

    // Apply year filters
    if (yearFromFilter) {
      const yearFrom = parseInt(yearFromFilter);
      filtered = filtered.filter(vehicle =>
        !vehicle.year_from || vehicle.year_from >= yearFrom
      );
    }

    if (yearToFilter) {
      const yearTo = parseInt(yearToFilter);
      filtered = filtered.filter(vehicle =>
        !vehicle.year_to || vehicle.year_to <= yearTo
      );
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = (a[vehicleSortBy] || "").toString();
      const bValue = (b[vehicleSortBy] || "").toString();
      
      // Handle numeric sorting for years
      if (vehicleSortBy === "year_from") {
        const aNum = a.year_from || 0;
        const bNum = b.year_from || 0;
        return vehicleSortOrder === "asc" ? aNum - bNum : bNum - aNum;
      }
      
      const comparison = aValue.localeCompare(bValue);
      return vehicleSortOrder === "asc" ? comparison : -comparison;
    });

    setFilteredVehicles(filtered);
    setVehicleCurrentPage(1); // Reset to first page when filters change
  }, [vehicleModels, vehicleSearchTerm, makeFilter, yearFromFilter, yearToFilter, vehicleSortBy, vehicleSortOrder]);

  // Filter and search effect for suggestions
  useEffect(() => {
    let filtered = [...suggestions];

    // Apply search filter
    if (suggestionsSearchTerm) {
      filtered = filtered.filter(suggestion =>
        suggestion.name?.toLowerCase().includes(suggestionsSearchTerm.toLowerCase()) ||
        suggestion.city?.toLowerCase().includes(suggestionsSearchTerm.toLowerCase()) ||
        suggestion.province?.toLowerCase().includes(suggestionsSearchTerm.toLowerCase()) ||
        suggestion.address?.toLowerCase().includes(suggestionsSearchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (suggestionsStatusFilter !== "all") {
      filtered = filtered.filter(suggestion => suggestion.status === suggestionsStatusFilter);
    }

    // Apply province filter
    if (suggestionsProvinceFilter) {
      filtered = filtered.filter(suggestion => suggestion.province === suggestionsProvinceFilter);
    }

    // Apply sorting
    filtered.sort((a, b) => {
      const aValue = (a[suggestionsSortBy] || "").toString();
      const bValue = (b[suggestionsSortBy] || "").toString();

      const comparison = aValue.localeCompare(bValue);
      return suggestionsSortOrder === "asc" ? comparison : -comparison;
    });

    setFilteredSuggestions(filtered);
    setSuggestionsCurrentPage(1); // Reset to first page when filters change
  }, [suggestions, suggestionsSearchTerm, suggestionsStatusFilter, suggestionsProvinceFilter, suggestionsSortBy, suggestionsSortOrder]);

  // Get unique values for filter dropdowns - Tyres
  const uniqueBrands = [...new Set(tyreDatabase.map(t => t.brand).filter(Boolean))].sort();
  const uniqueSizes = [...new Set(tyreDatabase.map(t => t.size).filter(Boolean))].sort();
  const uniqueTypes = [...new Set(tyreDatabase.map(t => t.type).filter(Boolean))].sort();

  // Get unique values for filter dropdowns - Vehicles
  const uniqueMakes = [...new Set(vehicleModels.map(v => v.make_name).filter(Boolean))].sort();
  const uniqueYearsFrom = [...new Set(vehicleModels.map(v => v.year_from).filter(Boolean))].sort((a, b) => (a || 0) - (b || 0));
  const uniqueYearsTo = [...new Set(vehicleModels.map(v => v.year_to).filter(Boolean))].sort((a, b) => (a || 0) - (b || 0));

  // Get unique values for filter dropdowns - Suggestions
  const uniqueProvinces = [...new Set(suggestions.map(s => s.province).filter(Boolean))].sort();
  const uniqueStatuses = [...new Set(suggestions.map(s => s.status).filter((status): status is string => Boolean(status)))].sort();

  // Pagination - Tyres
  const totalPages = Math.ceil(filteredTyres.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTyres = filteredTyres.slice(startIndex, startIndex + itemsPerPage);

  // Pagination - Vehicles
  const vehicleTotalPages = Math.ceil(filteredVehicles.length / vehicleItemsPerPage);
  const vehicleStartIndex = (vehicleCurrentPage - 1) * vehicleItemsPerPage;
  const paginatedVehicles = filteredVehicles.slice(vehicleStartIndex, vehicleStartIndex + vehicleItemsPerPage);

  // Pagination - Suggestions
  const suggestionsTotalPages = Math.ceil(filteredSuggestions.length / suggestionsItemsPerPage);
  const suggestionsStartIndex = (suggestionsCurrentPage - 1) * suggestionsItemsPerPage;
  const paginatedSuggestions = filteredSuggestions.slice(suggestionsStartIndex, suggestionsStartIndex + suggestionsItemsPerPage);

  const handleLogout = async () => {
    await logout();
    navigate("/admin-login");
  };

  const handleApproveApplication = async (id: string) => {
    const error = await setBusinessStatus(id, 'active');
    if (error) {
      console.error("Failed to approve business:", error);
      alert("Failed to approve business: " + error);
      return;
    }
    
    // Refresh applications and businesses
    const { data } = await supabase
      .from("businesses")
      .select("*")
      .eq("business_status", "pending")
      .order("created_at", { ascending: false });
    setApplications(data || []);
    
    // Refresh all businesses
    const { data: allData } = await getBusinessesByStatus();
    if (allData) {
      setAllBusinesses(allData);
    }
  };

  const handleRejectApplication = async (id: string) => {
    const error = await setBusinessStatus(id, 'blocked');
    if (error) {
      console.error("Failed to reject business:", error);
      alert("Failed to reject business: " + error);
      return;
    }
    
    // Refresh applications
    const { data } = await supabase
      .from("businesses")
      .select("*")
      .eq("business_status", "pending")
      .order("created_at", { ascending: false });
    setApplications(data || []);
    
    // Refresh all businesses
    const { data: allData } = await getBusinessesByStatus();
    if (allData) {
      setAllBusinesses(allData);
    }
  };

  const handleChangeBusinessStatus = async (businessId: string, newStatus: BusinessStatus, businessName?: string) => {
    // Get status info for confirmation
    const statusInfo = getBusinessStatusInfo(newStatus);
    const businessDisplayName = businessName || 'this business';
    
    // Show confirmation dialog for critical actions
    const isCriticalAction = newStatus === 'blocked' || newStatus === 'unpublished';
    if (isCriticalAction) {
      const actionDescription = newStatus === 'blocked'
        ? 'block this business (customers will not be able to see or contact them)'
        : 'unpublish this business (temporarily hide from customers)';
      
      const confirmed = window.confirm(
        `Are you sure you want to ${actionDescription}?\n\n` +
        `Business: ${businessDisplayName}\n` +
        `New Status: ${statusInfo.label}\n\n` +
        `This action can be reversed later.`
      );
      
      if (!confirmed) {
        return;
      }
    }
    
    // Validate business ID
    if (!businessId || businessId.trim() === '') {
      alert("Invalid business ID");
      return;
    }
    
    // Show loading state (you could add a loading indicator here)
    console.log(`Updating business ${businessId} to status: ${newStatus}`);
    
    try {
      const error = await setBusinessStatus(businessId, newStatus);
      if (error) {
        console.error("Failed to update business status:", error);
        alert(`Failed to update business status: ${error}`);
        return;
      }
      
      // Success feedback
      console.log(`Successfully updated business ${businessId} to ${newStatus}`);
      
      // Refresh all business data
      const { data: allData, error: fetchError } = await getBusinessesByStatus();
      if (fetchError) {
        console.error("Failed to refresh business data:", fetchError);
        alert("Status updated, but failed to refresh the list. Please reload the page.");
      } else if (allData) {
        setAllBusinesses(allData);
        
      }
      
      // Show success message
      const successMessage = `Successfully ${getActionVerb(newStatus)} "${businessDisplayName}"`;
      console.log(successMessage);
      
    } catch (err) {
      console.error("Unexpected error updating business status:", err);
      alert("An unexpected error occurred. Please try again.");
    }
  };
  
  // Helper function to get action verb for success messages
  const getActionVerb = (status: BusinessStatus): string => {
    switch (status) {
      case 'active': return 'activated';
      case 'blocked': return 'blocked';
      case 'unpublished': return 'unpublished';
      case 'pending': return 'set to pending';
      default: return 'updated';
    }
  };

  // CSV import logic is now at the top of the file

  const handleUploadTyreDatabase = () => {
    if (!newTyreFile) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const csv = e.target?.result;
      if (typeof csv !== "string") return;
      
      console.log("Starting CSV upload...");
      
      Papa.parse(csv, {
        header: true,
        skipEmptyLines: true,
        complete: async (results: Papa.ParseResult<Record<string, string>>) => {
          let successCount = 0;
          let errorCount = 0;
          const errors: string[] = [];
          
          console.log("CSV Headers:", Object.keys(results.data[0] || {}));
          console.log("Total rows:", results.data.length);
          
          for (let i = 0; i < results.data.length; i++) {
            const row = results.data[i];
            const r = row as Record<string, string>;
            
            console.log(`Processing row ${i + 1}:`, r);
            
            // More flexible column mapping
            const image_url = (r["Link to Image"] || r["image_url"] || r["Image"] || "").trim();
            const brand = (r["Tyre Brand"] || r["brand"] || r["Brand"] || "").trim();
            const name = (r["Tyre Name"] || r["name"] || r["Name"] || r["model"] || r["Model"] || "").trim();
            const size = (r["Size"] || r["size"] || "").trim();
            const type = (r["Type"] || r["type"] || r["tyre_type"] || "").trim();
            
            // Basic validation
            if (!brand) {
              errorCount++;
              errors.push(`Row ${i + 1}: Missing brand`);
              console.error(`Row ${i + 1}: Missing brand`, r);
              continue;
            }
            if (!name) {
              errorCount++;
              errors.push(`Row ${i + 1}: Missing name/model`);
              console.error(`Row ${i + 1}: Missing name/model`, r);
              continue;
            }
            if (!size) {
              errorCount++;
              errors.push(`Row ${i + 1}: Missing size`);
              console.error(`Row ${i + 1}: Missing size`, r);
              continue;
            }
            
            try {
              const { error } = await supabase
                .from("tyre_specifications")
                .insert([{ image_url, brand, name, size, type }] as any);

              if (error) {
                errorCount++;
                errors.push(`Row ${i + 1}: ${error.message}`);
                console.error(`Insert error for row ${i + 1}:`, error, r);
              } else {
                successCount++;
                console.log(`Row ${i + 1}: Successfully inserted`);
              }
            } catch (err) {
              errorCount++;
              errors.push(`Row ${i + 1}: Unexpected error`);
              console.error(`Unexpected error for row ${i + 1}:`, err, r);
            }
          }
          
          console.log(`Import complete. Success: ${successCount}, Errors: ${errorCount}`);
          console.log("Error details:", errors);
          
          // Show detailed feedback
          if (errorCount === 0) {
            alert(`✅ Import successful! Added ${successCount} tyres to the database.`);
          } else {
            const errorSummary = errors.slice(0, 10).join('\n');
            const moreErrors = errors.length > 10 ? `\n... and ${errors.length - 10} more errors` : '';
            alert(`Import complete.\n\n✅ Success: ${successCount}\n❌ Errors: ${errorCount}\n\nFirst 10 errors:\n${errorSummary}${moreErrors}\n\nCheck console for full details.`);
          }
        },
        error: (err: unknown) => {
          console.error("CSV parse error:", err);
          if (err instanceof Error) {
            alert("❌ CSV parse error: " + err.message);
          } else {
            alert("❌ CSV parse error - check file format");
          }
        }
      });
    };
    reader.readAsText(newTyreFile);
    setNewTyreFile(null);
  };

  const handleUploadVehicleDatabase = () => {
    if (!newVehicleFile) return;
    
    // Reset states
    setVehicleUploadProgress(0);
    setIsVehicleUploading(true);
    setUploadStatus('processing');
    setUploadResults(null);
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      const csv = e.target?.result;
      if (typeof csv !== "string") {
        setIsVehicleUploading(false);
        setUploadStatus('error');
        return;
      }
      
      console.log("Starting Vehicle CSV upload...");
      
      Papa.parse(csv, {
        header: true,
        skipEmptyLines: true,
        complete: async (results: Papa.ParseResult<Record<string, string>>) => {
          let successCount = 0;
          let errorCount = 0;
          const errors: string[] = [];
          const makeCache = new Map<string, string>(); // Cache for make name -> ID mapping
          const totalRows = results.data.length;
          
          console.log("Vehicle CSV Headers:", Object.keys(results.data[0] || {}));
          console.log("Total rows:", totalRows);
          
          for (let i = 0; i < results.data.length; i++) {
            const row = results.data[i];
            const r = row as Record<string, string>;
            
            // Update progress
            const progress = Math.round(((i + 1) / totalRows) * 100);
            setVehicleUploadProgress(progress);
            
            console.log(`Processing row ${i + 1}:`, r);
            
            // More flexible column mapping for vehicle data
            const makeName = (r["Make"] || r["Manufacturer"] || r["Brand"] || r["make"] || r["manufacturer"] || "").trim();
            const modelName = (r["Model"] || r["Name"] || r["Variant"] || r["model"] || r["name"] || "").trim();
            const yearFromStr = (r["Year From"] || r["Start Year"] || r["year_from"] || r["Year"] || "").trim();
            const yearToStr = (r["Year To"] || r["End Year"] || r["year_to"] || "").trim();
            const defaultTyreSize = (r["Default Tyre Size"] || r["Tyre Size"] || r["Recommended Size"] || r["default_tyre_size"] || "").trim();
            
            // Basic validation
            if (!makeName) {
              errorCount++;
              errors.push(`Row ${i + 1}: Missing manufacturer/make`);
              console.error(`Row ${i + 1}: Missing manufacturer/make`, r);
              continue;
            }
            if (!modelName) {
              errorCount++;
              errors.push(`Row ${i + 1}: Missing model name`);
              console.error(`Row ${i + 1}: Missing model name`, r);
              continue;
            }
            
            try {
              // Get or create vehicle make
              let makeId = makeCache.get(makeName);
              if (!makeId) {
                // Check if make already exists
                const { data: existingMake } = await supabase
                  .from("vehicle_makes")
                  .select("id")
                  .eq("name", makeName)
                  .maybeSingle();
                
                if (existingMake && (existingMake as any).id) {
                  makeId = (existingMake as any).id;
                } else {
                  // Create new make
                  const { data: newMake, error: makeError } = await supabase
                    .from("vehicle_makes")
                    .insert({
                      name: makeName,
                      popular: ['Toyota', 'BMW', 'Mercedes-Benz', 'Audi', 'Volkswagen', 'Ford', 'Honda', 'Nissan', 'Hyundai', 'Kia'].includes(makeName)
                    } as any)
                    .select("id")
                    .single();
                  
                  if (makeError || !newMake) {
                    errorCount++;
                    errors.push(`Row ${i + 1}: Failed to create make "${makeName}": ${makeError?.message || 'Unknown error'}`);
                    continue;
                  }
                  makeId = (newMake as any).id;
                }
                if (makeId) {
                  makeCache.set(makeName, makeId);
                }
              }
              
              // Parse years
              const yearFrom = yearFromStr ? parseInt(yearFromStr) : null;
              const yearTo = yearToStr ? parseInt(yearToStr) : null;
              
              // Validate year range
              if (yearFrom && yearTo && yearFrom > yearTo) {
                errorCount++;
                errors.push(`Row ${i + 1}: Invalid year range (${yearFrom} > ${yearTo})`);
                continue;
              }
              
              // Insert vehicle model
              const modelData = {
                make_id: makeId!,
                name: modelName,
                ...(yearFrom && { year_from: yearFrom }),
                ...(yearTo && { year_to: yearTo }),
                ...(defaultTyreSize && { default_tyre_size: defaultTyreSize })
              };
              
              const { error: modelError } = await supabase
                .from("vehicle_models")
                .insert(modelData as any);

              if (modelError) {
                errorCount++;
                errors.push(`Row ${i + 1}: ${modelError.message}`);
                console.error(`Insert error for row ${i + 1}:`, modelError, r);
              } else {
                successCount++;
                console.log(`Row ${i + 1}: Successfully inserted ${makeName} ${modelName}`);
              }
            } catch (err) {
              errorCount++;
              errors.push(`Row ${i + 1}: Unexpected error`);
              console.error(`Unexpected error for row ${i + 1}:`, err, r);
            }
          }
          
          console.log(`Vehicle import complete. Success: ${successCount}, Errors: ${errorCount}`);
          
          // Set final results
          setUploadResults({
            successCount,
            errorCount,
            errors,
            totalRows
          });
          
          setUploadStatus(errorCount === 0 ? 'success' : (successCount > 0 ? 'success' : 'error'));
          setIsVehicleUploading(false);
          setVehicleUploadProgress(100);
          
          // Refresh vehicle data
          const { data: makesData } = await supabase
            .from("vehicle_makes")
            .select("*")
            .order("name", { ascending: true });
          setVehicleMakes((makesData || []).map(make => ({
            ...make,
            popular: make.popular ?? undefined,
            created_at: make.created_at ?? undefined
          })));

          const { data: modelsData } = await supabase
            .from("vehicle_models")
            .select(`
              *,
              vehicle_makes!inner(name)
            `)
            .order("created_at", { ascending: false });

          if (modelsData) {
            const vehicles = modelsData.map((model: any) => ({
              ...model,
              make_name: model.vehicle_makes?.name || 'Unknown'
            }));
            setVehicleModels(vehicles);
            setFilteredVehicles(vehicles);
          }
        },
        error: (err: unknown) => {
          console.error("Vehicle CSV parse error:", err);
          setUploadStatus('error');
          setIsVehicleUploading(false);
          setUploadResults({
            successCount: 0,
            errorCount: 1,
            errors: [err instanceof Error ? err.message : 'CSV parse error - check file format'],
            totalRows: 0
          });
        }
      });
    };
    reader.readAsText(newVehicleFile);
    setNewVehicleFile(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleArchiveSuggestion = async (suggestionId: string) => {
    if (!window.confirm('Are you sure you want to archive this suggestion? It will be marked as archived and hidden from the main list.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('fitment_centre_suggestions')
        .update({ status: 'archived', updated_at: new Date().toISOString() })
        .eq('id', suggestionId);

      if (error) {
        alert('Failed to archive suggestion: ' + error.message);
        return;
      }

      // Refresh suggestions data
      const { data, error: fetchError } = await supabase
        .from("fitment_centre_suggestions")
        .select("*")
        .order("created_at", { ascending: false });

      if (fetchError) {
        setSuggestionsError(fetchError.message);
      } else {
        setSuggestions(data || []);
        setFilteredSuggestions(data || []);
      }

      setSuggestionModalOpen(false);
      setSelectedSuggestion(null);
      alert('Suggestion archived successfully');
    } catch (err) {
      alert('An unexpected error occurred while archiving the suggestion');
    }
  };

  const tabs = [
    { id: "overview", label: "Overview & Analytics" },
    { id: "users", label: "Users & Registrations" },
    { id: "businesses", label: "Business Management", hasDropdown: true, subItems: [
      { id: "applications", label: "Business Applications" },
      { id: "revenue", label: "Revenue Management" }
    ]},
    { id: "chat", label: "Chat Support" },
    { id: "reviews", label: "Reviews" },
    { id: "tyres", label: "Central Tyre Database" },
    { id: "vehicles", label: "Vehicle Database" },
    { id: "suggestions", label: "Tyre Fitment Center Suggestions" },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">TyreLink Platform Admin</h1>
              <p className="text-gray-600 mt-1">Manage the entire TyreLink ecosystem</p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                Platform Revenue: <span className="font-semibold text-green-600">
                  {analyticsLoading ? "..." : formatCurrency(analytics.monthlyRevenue)}
                </span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-sm text-gray-600">
                  Welcome Suren!
                </div>
                <button
                  onClick={handleLogout}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-700 focus:ring-2 focus:ring-red-500"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => {
              if (tab.hasDropdown) {
                return (
                  <div key={tab.id} className="relative" onMouseEnter={() => setDropdownOpen(true)} onMouseLeave={() => setDropdownOpen(false)}>
                    <button
                      onClick={() => {
                        setActiveTab(tab.id);
                        setDropdownOpen(!dropdownOpen);
                      }}
                      className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-1 ${
                        activeTab === tab.id || activeTab === 'applications' || activeTab === 'revenue'
                          ? "border-blue-500 text-blue-600"
                          : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                      }`}
                    >
                      <span>{tab.label}</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {dropdownOpen && (
                      <div className="absolute top-full left-0 bg-white border border-gray-200 shadow-lg rounded-md z-10 min-w-max">
                        {tab.subItems.map((sub) => (
                          <button
                            key={sub.id}
                            onClick={() => {
                              setActiveTab(sub.id);
                              setDropdownOpen(false);
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              } else {
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === tab.id
                        ? "border-blue-500 text-blue-600"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    {tab.label}
                  </button>
                );
              }
            })}
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Suggestion View Modal */}
        {suggestionModalOpen && selectedSuggestion && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold">Fitment Center Suggestion Details</h3>
                <button
                  onClick={() => {
                    setSuggestionModalOpen(false);
                    setSelectedSuggestion(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Business Details */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Business Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Business Name</label>
                      <p className="text-sm text-gray-900">{selectedSuggestion.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <p className="text-sm text-gray-900">{selectedSuggestion.address}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">City</label>
                      <p className="text-sm text-gray-900">{selectedSuggestion.city}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Province</label>
                      <p className="text-sm text-gray-900">{selectedSuggestion.province}</p>
                    </div>
                  </div>
                  {selectedSuggestion.additional_info && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700">Additional Information</label>
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedSuggestion.additional_info}</p>
                    </div>
                  )}
                </div>

                {/* Contact Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Phone</label>
                      <p className="text-sm text-gray-900">{selectedSuggestion.phone || 'Not provided'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Email</label>
                      <p className="text-sm text-gray-900">{selectedSuggestion.email || 'Not provided'}</p>
                    </div>
                  </div>
                </div>

                {/* Status and Review Information */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Review Status</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Current Status</label>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        (selectedSuggestion.status === 'submitted' || !selectedSuggestion.status || selectedSuggestion.status === 'pending') ? 'bg-blue-100 text-blue-800' :
                        selectedSuggestion.status === 'read' ? 'bg-green-100 text-green-800' :
                        selectedSuggestion.status === 'archived' ? 'bg-gray-100 text-gray-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {(selectedSuggestion.status === 'submitted' || !selectedSuggestion.status || selectedSuggestion.status === 'pending') ? 'Submitted' :
                         selectedSuggestion.status === 'read' ? 'Read' :
                         selectedSuggestion.status === 'archived' ? 'Archived' :
                         selectedSuggestion.status.charAt(0).toUpperCase() + selectedSuggestion.status.slice(1)}
                      </span>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Submitted Date</label>
                      <p className="text-sm text-gray-900">
                        {selectedSuggestion.created_at ? formatDate(selectedSuggestion.created_at) : 'Unknown'}
                      </p>
                    </div>
                  </div>
                  {selectedSuggestion.review_notes && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700">Review Notes</label>
                      <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedSuggestion.review_notes}</p>
                    </div>
                  )}
                  {selectedSuggestion.reviewed_at && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700">Reviewed Date</label>
                      <p className="text-sm text-gray-900">{formatDate(selectedSuggestion.reviewed_at)}</p>
                    </div>
                  )}
                  {selectedSuggestion.reviewed_by && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700">Reviewed By</label>
                      <p className="text-sm text-gray-900">{selectedSuggestion.reviewed_by}</p>
                    </div>
                  )}
                </div>

                {/* Location Information */}
                {selectedSuggestion.suggested_by_location && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-2">Suggested By Location</h4>
                    <p className="text-sm text-gray-900">{selectedSuggestion.suggested_by_location}</p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => {
                      setSuggestionModalOpen(false);
                      setSelectedSuggestion(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => handleArchiveSuggestion(selectedSuggestion.id)}
                    className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                  >
                    Archive Suggestion
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-sm font-medium text-gray-500">Total Users</h3>
                <p className="text-3xl font-bold text-blue-600">
                  {analyticsLoading ? "..." : analytics.totalUsers.toLocaleString()}
                </p>
                <p className="text-sm text-green-600 mt-1">
                  {analyticsLoading
                    ? "..."
                    : (typeof analytics.platformGrowth === "number" && analytics.platformGrowth !== 0)
                      ? `+${analytics.platformGrowth}% this month`
                      : "Growth data unavailable"}
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-sm font-medium text-gray-500">Active Businesses</h3>
                <p className="text-3xl font-bold text-green-600">
                  {analyticsLoading ? "..." : analytics.totalBusinesses}
                </p>
                <p className="text-sm text-blue-600 mt-1">
                  {applicationsLoading ? "..." : applications.length} pending approval
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-sm font-medium text-gray-500">Monthly Revenue</h3>
                <p className="text-3xl font-bold text-purple-600">
                  {analyticsLoading ? "..." : formatCurrency(analytics.monthlyRevenue)}
                </p>
                <p className="text-sm text-green-600 mt-1">From subscription fees</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-sm font-medium text-gray-500">Total Bookings</h3>
                <p className="text-3xl font-bold text-orange-600">
                  {analyticsLoading ? "..." : analytics.totalBookings}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {analyticsLoading
                    ? "..."
                    : (typeof analytics.activeSearches === "number" && analytics.activeSearches > 0)
                      ? `${analytics.activeSearches} active searches`
                      : "No search activity yet"}
                </p>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Platform Activity Overview</h3>
              <div className="space-y-4">
                {/* Recent Business Applications */}
                {allBusinesses.filter(b => b.business_status === 'pending').slice(0, 3).map((biz) => (
                  <div key={biz.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">New business application</p>
                      <p className="text-sm text-gray-600">{biz.name} submitted application</p>
                    </div>
                    <span className="text-sm text-gray-500">{formatDate(biz.created_at)}</span>
                  </div>
                ))}
                {/* Recent Payments */}
                {/* You may need to fetch recent payments into a state variable, e.g. recentPayments */}
                {Array.isArray(recentPayments) && recentPayments.length > 0 && recentPayments.slice(0, 3).map((pay) => (
                  <div key={pay.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Payment received</p>
                      <p className="text-sm text-gray-600">{pay.business_name || "A business"} paid monthly subscription</p>
                    </div>
                    <span className="text-sm text-gray-500">{formatDate(pay.created_at)}</span>
                  </div>
                ))}
                {/* Recent Tyre DB Updates */}
                {/* You may need to fetch recent tyre updates into a state variable, e.g. recentTyreUpdates */}
                {Array.isArray(recentTyreUpdates) && recentTyreUpdates.length > 0 && recentTyreUpdates.slice(0, 3).map((tyre) => (
                  <div key={tyre.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium">Tyre database updated</p>
                      <p className="text-sm text-gray-600">Added {tyre.count || 1} new tyre models to central database</p>
                    </div>
                    <span className="text-sm text-gray-500">{formatDate(tyre.created_at)}</span>
                  </div>
                ))}
                {/* Fallback if no activity */}
                {(!allBusinesses.length && (!recentPayments || !recentPayments.length) && (!recentTyreUpdates || !recentTyreUpdates.length)) && (
                  <div className="text-center text-gray-500 py-8">No recent platform activity.</div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* Business Applications Tab */}
        {activeTab === "applications" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold">Pending Business Applications</h3>
                <p className="text-gray-600">Review and approve new tyre shop applications</p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Services</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applied</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {applicationsLoading && (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-gray-500">Loading applications...</td>
                      </tr>
                    )}
                    {applicationsError && (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-red-500">{applicationsError}</td>
                      </tr>
                    )}
                    {!applicationsLoading && !applicationsError && applications.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-gray-500">No pending applications found.</td>
                      </tr>
                    )}
                    {!applicationsLoading && !applicationsError && applications.map((app) => (
                      <tr key={app.id}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900">{app.name}</span>
                              <a
                                href={`/business/${app.slug || app.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 text-xs underline hover:text-blue-800"
                                title="View Shop Page"
                              >
                                View Shop
                              </a>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                getBusinessStatusInfo(app.business_status || 'pending').color
                              }`}>
                                {getBusinessStatusInfo(app.business_status || 'pending').label}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500">{app.description}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{app.contact_email}</div>
                          <div className="text-sm text-gray-500">{app.contact_phone}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {Array.isArray(app.services) ? app.services.slice(0, 2).join(", ") : ""}
                            {Array.isArray(app.services) && app.services.length > 2 && ` +${app.services.length - 2} more`}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                          <div className="text-xs text-gray-500 mt-1">R499/month</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(app.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                          <button
                            onClick={() => handleApproveApplication(app.id)}
                            className="bg-green-600 text-white px-3 py-1 rounded text-xs hover:bg-green-700"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => handleRejectApplication(app.id)}
                            className="bg-red-600 text-white px-3 py-1 rounded text-xs hover:bg-red-700"
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {/* Business Management Tab */}
        {activeTab === "businesses" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold">Business Management</h3>
                <p className="text-gray-600">Manage all businesses, update statuses, and monitor activity</p>
              </div>
              
              {/* Filters and Search */}
              <div className="p-6 border-b bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Status</label>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value as BusinessStatus | 'all')}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Statuses</option>
                      {Object.entries(BUSINESS_STATUS_CONFIG).map(([status, config]) => (
                        <option key={status} value={status}>
                          {config.label} ({allBusinesses.filter(b => b.business_status === status).length})
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search Businesses</label>
                    <input
                      type="text"
                      placeholder="Search by name, email, or description..."
                      value={businessSearchTerm}
                      onChange={(e) => setBusinessSearchTerm(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                
                {/* Status Summary */}
                <div className="mt-4 flex flex-wrap gap-2">
                  <div className="text-sm text-gray-600">
                    Showing {filteredBusinesses.length} of {allBusinesses.length} businesses
                  </div>
                  <div className="flex space-x-4 text-sm">
                    {Object.entries(BUSINESS_STATUS_CONFIG).map(([status, config]) => {
                      const count = allBusinesses.filter(b => b.business_status === status).length;
                      return (
                        <span key={status} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                          {config.label}: {count}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {businessesLoading && (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-gray-500">Loading businesses...</td>
                      </tr>
                    )}
                    {!businessesLoading && filteredBusinesses.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-gray-500">
                          {statusFilter === 'all' ? 'No businesses found.' : `No businesses found with status "${BUSINESS_STATUS_CONFIG[statusFilter as BusinessStatus]?.label}".`}
                        </td>
                      </tr>
                    )}
                    {!businessesLoading && filteredBusinesses.map((business) => (
                      <tr key={business.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-medium text-gray-900">{business.name}</span>
                              <a
                                href={`/business/${business.slug || business.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600 text-xs underline hover:text-blue-800"
                                title="View Shop Page"
                              >
                                View Shop
                              </a>
                            </div>
                            <div className="text-sm text-gray-500">{business.description}</div>
                            {business.services && Array.isArray(business.services) && (
                              <div className="text-xs text-gray-400 mt-1">
                                Services: {business.services.slice(0, 2).join(", ")}
                                {business.services.length > 2 && ` +${business.services.length - 2} more`}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{business.contact_email}</div>
                          <div className="text-sm text-gray-500">{business.contact_phone}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            getBusinessStatusInfo(business.business_status || 'pending').color
                          }`}>
                            {getBusinessStatusInfo(business.business_status || 'pending').label}
                          </span>
                          {business.monthly_fee && (
                            <div className="text-xs text-gray-500 mt-1">{formatCurrency(business.monthly_fee)}/month</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(business.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex flex-col space-y-1">
                            {/* Status Change Buttons */}
                            {business.business_status === 'pending' && (
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => handleChangeBusinessStatus(business.id, 'active', business.name)}
                                  className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                                  title="Approve Business"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => handleChangeBusinessStatus(business.id, 'blocked', business.name)}
                                  className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                                  title="Block Business"
                                >
                                  Block
                                </button>
                              </div>
                            )}
                            
                            {business.business_status === 'active' && (
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => handleChangeBusinessStatus(business.id, 'unpublished', business.name)}
                                  className="bg-yellow-600 text-white px-2 py-1 rounded text-xs hover:bg-yellow-700"
                                  title="Unpublish Business"
                                >
                                  Unpublish
                                </button>
                                <button
                                  onClick={() => handleChangeBusinessStatus(business.id, 'blocked', business.name)}
                                  className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                                  title="Block Business"
                                >
                                  Block
                                </button>
                              </div>
                            )}
                            
                            {business.business_status === 'blocked' && (
                              <button
                                onClick={() => handleChangeBusinessStatus(business.id, 'active', business.name)}
                                className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                                title="Reinstate Business"
                              >
                                Reinstate
                              </button>
                            )}
                            
                            {business.business_status === 'unpublished' && (
                              <div className="flex space-x-1">
                                <button
                                  onClick={() => handleChangeBusinessStatus(business.id, 'active', business.name)}
                                  className="bg-green-600 text-white px-2 py-1 rounded text-xs hover:bg-green-700"
                                  title="Republish Business"
                                >
                                  Republish
                                </button>
                                <button
                                  onClick={() => handleChangeBusinessStatus(business.id, 'blocked', business.name)}
                                  className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
                                  title="Block Business"
                                >
                                  Block
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}


        {/* Central Tyre Database Tab */}
        {activeTab === "tyres" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Upload Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Upload Central Tyre Database</h3>
              <p className="text-gray-600 mb-4">
                Upload a spreadsheet to update the central tyre database. All businesses will be able to choose from these tyres.
              </p>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="mt-4">
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        Drop files here or click to upload
                      </span>
                      <input
                        id="file-upload"
                        type="file"
                        className="sr-only"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => setNewTyreFile(e.target.files?.[0] || null)}
                      />
                    </label>
                    <p className="mt-2 text-xs text-gray-500">Excel or CSV files up to 10MB</p>
                  </div>
                </div>
              </div>
              {newTyreFile && (
                <div className="mt-4 flex items-center justify-between bg-blue-50 p-4 rounded-lg">
                  <span className="text-sm text-blue-800">Selected: {newTyreFile.name}</span>
                  <button
                    onClick={handleUploadTyreDatabase}
                    className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700"
                  >
                    Upload Database
                  </button>
                </div>
              )}
            </div>

            {/* Current Database with Search and Filtering */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold">Central Tyre Database ({filteredTyres.length} tyres)</h3>
                <p className="text-gray-600">Search, filter and manage the central tyre database that sellers can choose from</p>
              </div>
              
              {/* Search and Filter Controls */}
              <div className="p-6 border-b bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  {/* Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                    <input
                      type="text"
                      placeholder="Search brand, model, size..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  {/* Brand Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Brand</label>
                    <select
                      value={brandFilter}
                      onChange={(e) => setBrandFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Brands</option>
                      {uniqueBrands.map(brand => (
                        <option key={brand} value={brand}>{brand}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Size Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Size</label>
                    <select
                      value={sizeFilter}
                      onChange={(e) => setSizeFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Sizes</option>
                      {uniqueSizes.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      value={typeFilter}
                      onChange={(e) => setTypeFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Types</option>
                      {uniqueTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  {/* Sort Controls */}
                  <div className="flex items-center space-x-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
                      <select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value as any)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="brand">Brand</option>
                        <option value="name">Model</option>
                        <option value="size">Size</option>
                        <option value="type">Type</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                      <select
                        value={sortOrder}
                        onChange={(e) => setSortOrder(e.target.value as any)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="asc">A-Z</option>
                        <option value="desc">Z-A</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Items per page */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Show</label>
                    <select
                      value={itemsPerPage}
                      onChange={(e) => setItemsPerPage(Number(e.target.value))}
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => {
                            setSortBy("brand");
                            setSortOrder(sortBy === "brand" && sortOrder === "asc" ? "desc" : "asc");
                          }}
                          className="flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>Brand</span>
                          {sortBy === "brand" && (
                            <span className="text-blue-500">
                              {sortOrder === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => {
                            setSortBy("name");
                            setSortOrder(sortBy === "name" && sortOrder === "asc" ? "desc" : "asc");
                          }}
                          className="flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>Model</span>
                          {sortBy === "name" && (
                            <span className="text-blue-500">
                              {sortOrder === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => {
                            setSortBy("size");
                            setSortOrder(sortBy === "size" && sortOrder === "asc" ? "desc" : "asc");
                          }}
                          className="flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>Size</span>
                          {sortBy === "size" && (
                            <span className="text-blue-500">
                              {sortOrder === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => {
                            setSortBy("type");
                            setSortOrder(sortBy === "type" && sortOrder === "asc" ? "desc" : "asc");
                          }}
                          className="flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>Type</span>
                          {sortBy === "type" && (
                            <span className="text-blue-500">
                              {sortOrder === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available to Sellers</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {tyreLoading && (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-gray-500">Loading tyre database...</td>
                      </tr>
                    )}
                    {tyreError && (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-red-500">{tyreError}</td>
                      </tr>
                    )}
                    {!tyreLoading && !tyreError && filteredTyres.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-gray-500">No tyres found matching current filters.</td>
                      </tr>
                    )}
                    {!tyreLoading && !tyreError && paginatedTyres.map((tyre) => (
                      <tr key={tyre.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{tyre.brand}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tyre.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tyre.size}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            tyre.type ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'
                          }`}>
                            {tyre.type || 'Not specified'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                          ✓ Available for seller selection
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredTyres.length)} of {filteredTyres.length} tyres
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-2 text-sm">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Vehicle Database Tab */}
        {activeTab === "vehicles" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Upload Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4">Upload Vehicle Database</h3>
              <p className="text-gray-600 mb-4">
                Upload a spreadsheet to update the vehicle database. This will populate manufacturers and models for vehicle search functionality.
              </p>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                <div className="text-center">
                  <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div className="mt-4">
                    <label htmlFor="vehicle-file-upload" className="cursor-pointer">
                      <span className="mt-2 block text-sm font-medium text-gray-900">
                        Drop vehicle files here or click to upload
                      </span>
                      <input
                        id="vehicle-file-upload"
                        type="file"
                        className="sr-only"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => setNewVehicleFile(e.target.files?.[0] || null)}
                      />
                    </label>
                    <p className="mt-2 text-xs text-gray-500">Excel or CSV files with Make, Model, Year columns</p>
                  </div>
                </div>
              </div>
              {newVehicleFile && (
                <div className="mt-4 flex items-center justify-between bg-green-50 p-4 rounded-lg">
                  <span className="text-sm text-green-800">Selected: {newVehicleFile.name}</span>
                  <button
                    onClick={handleUploadVehicleDatabase}
                    disabled={isVehicleUploading}
                    className="bg-green-600 text-white px-4 py-2 rounded text-sm hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isVehicleUploading ? 'Processing...' : 'Upload Vehicle Database'}
                  </button>
                </div>
              )}

              {/* Upload Progress */}
              {isVehicleUploading && (
                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-blue-900">Processing Vehicle Data</span>
                    <span className="text-sm text-blue-700">{vehicleUploadProgress}%</span>
                  </div>
                  <div className="w-full bg-blue-100 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${vehicleUploadProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-blue-600 mt-2">
                    {uploadStatus === 'processing' ? 'Validating and inserting vehicle records...' : 'Processing complete'}
                  </p>
                </div>
              )}

              {/* Upload Results */}
              {uploadResults && uploadStatus !== 'idle' && (
                <div className={`mt-4 border rounded-lg p-4 ${
                  uploadStatus === 'success'
                    ? 'bg-green-50 border-green-200'
                    : uploadStatus === 'error'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-yellow-50 border-yellow-200'
                }`}>
                  <div className="flex items-start">
                    <div className={`flex-shrink-0 ${
                      uploadStatus === 'success' ? 'text-green-400' :
                      uploadStatus === 'error' ? 'text-red-400' : 'text-yellow-400'
                    }`}>
                      {uploadStatus === 'success' && (
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      )}
                      {uploadStatus === 'error' && (
                        <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                      )}
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className={`text-sm font-medium ${
                        uploadStatus === 'success' ? 'text-green-800' :
                        uploadStatus === 'error' ? 'text-red-800' : 'text-yellow-800'
                      }`}>
                        {uploadStatus === 'success' && uploadResults.errorCount === 0
                          ? `✅ Upload Successful!`
                          : uploadStatus === 'success' && uploadResults.errorCount > 0
                          ? `⚠️ Upload Completed with Issues`
                          : `❌ Upload Failed`}
                      </h3>
                      <div className={`mt-2 text-sm ${
                        uploadStatus === 'success' ? 'text-green-700' :
                        uploadStatus === 'error' ? 'text-red-700' : 'text-yellow-700'
                      }`}>
                        <div className="grid grid-cols-2 gap-4 mb-3">
                          <div className="bg-white bg-opacity-50 rounded p-2">
                            <div className="text-xs opacity-75">Successfully Processed</div>
                            <div className="text-lg font-bold text-green-600">{uploadResults.successCount}</div>
                          </div>
                          <div className="bg-white bg-opacity-50 rounded p-2">
                            <div className="text-xs opacity-75">Errors Encountered</div>
                            <div className="text-lg font-bold text-red-600">{uploadResults.errorCount}</div>
                          </div>
                        </div>
                        
                        {uploadResults.errorCount > 0 && (
                          <div className="mt-3">
                            <div className="text-xs font-medium mb-2">Error Details:</div>
                            <div className="bg-white bg-opacity-75 rounded p-2 text-xs max-h-32 overflow-y-auto">
                              {uploadResults.errors.slice(0, 5).map((error, idx) => (
                                <div key={idx} className="mb-1 text-red-700">• {error}</div>
                              ))}
                              {uploadResults.errors.length > 5 && (
                                <div className="text-red-600 font-medium">
                                  ... and {uploadResults.errors.length - 5} more errors
                                </div>
                              )}
                            </div>
                            <div className="text-xs mt-1 opacity-75">
                              Check browser console for complete error details
                            </div>
                          </div>
                        )}
                        
                        {uploadResults.successCount > 0 && (
                          <div className="text-xs mt-2 opacity-75">
                            {uploadResults.successCount} vehicle models have been added to the database and are now available for search.
                          </div>
                        )}
                      </div>
                      
                      <button
                        onClick={() => {
                          setUploadResults(null);
                          setUploadStatus('idle');
                        }}
                        className={`mt-3 text-xs px-2 py-1 rounded ${
                          uploadStatus === 'success' ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                          'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Current Database with Search and Filtering */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold">Vehicle Database ({filteredVehicles.length} models)</h3>
                <p className="text-gray-600">Search, filter and manage vehicle manufacturers and models for vehicle search functionality</p>
              </div>
              
              {/* Search and Filter Controls */}
              <div className="p-6 border-b bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  {/* Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                    <input
                      type="text"
                      placeholder="Search make, model..."
                      value={vehicleSearchTerm}
                      onChange={(e) => setVehicleSearchTerm(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  {/* Make Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Manufacturer</label>
                    <select
                      value={makeFilter}
                      onChange={(e) => setMakeFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Manufacturers</option>
                      {uniqueMakes.map(make => (
                        <option key={make} value={make}>{make}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Year From Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year From</label>
                    <select
                      value={yearFromFilter}
                      onChange={(e) => setYearFromFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Any Year</option>
                      {uniqueYearsFrom.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                  
                  {/* Year To Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Year To</label>
                    <select
                      value={yearToFilter}
                      onChange={(e) => setYearToFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">Any Year</option>
                      {uniqueYearsTo.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  {/* Sort Controls */}
                  <div className="flex items-center space-x-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
                      <select
                        value={vehicleSortBy}
                        onChange={(e) => setVehicleSortBy(e.target.value as any)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="make_name">Manufacturer</option>
                        <option value="name">Model</option>
                        <option value="year_from">Year</option>
                        <option value="default_tyre_size">Tyre Size</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                      <select
                        value={vehicleSortOrder}
                        onChange={(e) => setVehicleSortOrder(e.target.value as any)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="asc">A-Z / Low-High</option>
                        <option value="desc">Z-A / High-Low</option>
                      </select>
                    </div>
                  </div>
                  
                  {/* Items per page */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Show</label>
                    <select
                      value={vehicleItemsPerPage}
                      onChange={(e) => setVehicleItemsPerPage(Number(e.target.value))}
                      className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => {
                            setVehicleSortBy("make_name");
                            setVehicleSortOrder(vehicleSortBy === "make_name" && vehicleSortOrder === "asc" ? "desc" : "asc");
                          }}
                          className="flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>Manufacturer</span>
                          {vehicleSortBy === "make_name" && (
                            <span className="text-blue-500">
                              {vehicleSortOrder === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => {
                            setVehicleSortBy("name");
                            setVehicleSortOrder(vehicleSortBy === "name" && vehicleSortOrder === "asc" ? "desc" : "asc");
                          }}
                          className="flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>Model</span>
                          {vehicleSortBy === "name" && (
                            <span className="text-blue-500">
                              {vehicleSortOrder === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => {
                            setVehicleSortBy("year_from");
                            setVehicleSortOrder(vehicleSortBy === "year_from" && vehicleSortOrder === "asc" ? "desc" : "asc");
                          }}
                          className="flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>Year Range</span>
                          {vehicleSortBy === "year_from" && (
                            <span className="text-blue-500">
                              {vehicleSortOrder === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <button
                          onClick={() => {
                            setVehicleSortBy("default_tyre_size");
                            setVehicleSortOrder(vehicleSortBy === "default_tyre_size" && vehicleSortOrder === "asc" ? "desc" : "asc");
                          }}
                          className="flex items-center space-x-1 hover:text-gray-700"
                        >
                          <span>Default Tyre Size</span>
                          {vehicleSortBy === "default_tyre_size" && (
                            <span className="text-blue-500">
                              {vehicleSortOrder === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </button>
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available for Search</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {vehicleLoading && (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-gray-500">Loading vehicle database...</td>
                      </tr>
                    )}
                    {vehicleError && (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-red-500">{vehicleError}</td>
                      </tr>
                    )}
                    {!vehicleLoading && !vehicleError && filteredVehicles.length === 0 && (
                      <tr>
                        <td colSpan={5} className="text-center py-8 text-gray-500">No vehicles found matching current filters.</td>
                      </tr>
                    )}
                    {!vehicleLoading && !vehicleError && paginatedVehicles.map((vehicle) => (
                      <tr key={vehicle.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{vehicle.make_name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{vehicle.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {vehicle.year_from || vehicle.year_to ? (
                            <span>
                              {vehicle.year_from || "?"} - {vehicle.year_to || "Present"}
                            </span>
                          ) : (
                            <span className="text-gray-400">Not specified</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {vehicle.default_tyre_size ? (
                            <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                              {vehicle.default_tyre_size}
                            </span>
                          ) : (
                            <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-500">
                              Not specified
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                          ✓ Available for vehicle search
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination */}
              {vehicleTotalPages > 1 && (
                <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Showing {vehicleStartIndex + 1} to {Math.min(vehicleStartIndex + vehicleItemsPerPage, filteredVehicles.length)} of {filteredVehicles.length} vehicles
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setVehicleCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={vehicleCurrentPage === 1}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-2 text-sm">
                      Page {vehicleCurrentPage} of {vehicleTotalPages}
                    </span>
                    <button
                      onClick={() => setVehicleCurrentPage(prev => Math.min(vehicleTotalPages, prev + 1))}
                      disabled={vehicleCurrentPage === vehicleTotalPages}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Database Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-sm font-medium text-gray-500">Total Manufacturers</h3>
                <p className="text-3xl font-bold text-blue-600">
                  {vehicleMakes.length}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {vehicleMakes.filter(m => m.popular).length} popular brands
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-sm font-medium text-gray-500">Total Models</h3>
                <p className="text-3xl font-bold text-green-600">
                  {vehicleModels.length}
                </p>
                <p className="text-sm text-gray-600 mt-1">
                  {vehicleModels.filter(v => v.default_tyre_size).length} with tyre size data
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-sm font-medium text-gray-500">Search Ready</h3>
                <p className="text-3xl font-bold text-purple-600">
                  {Math.round((vehicleModels.filter(v => v.default_tyre_size).length / Math.max(vehicleModels.length, 1)) * 100)}%
                </p>
                <p className="text-sm text-gray-600 mt-1">Models with tyre compatibility</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Revenue Management Tab */}
        {activeTab === "revenue" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Subscription Revenue */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold mb-4">Subscription Revenue</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monthly platform subscriptions</span>
                    <span className="font-semibold">
                      {analyticsLoading ? "..." : formatCurrency(analytics.monthlyRevenue)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>Active businesses: {analytics.totalBusinesses}</span>
                    <span>Platform fee per business: {formatCurrency(analytics.monthlyRevenue / Math.max(analytics.totalBusinesses, 1))}</span>
                  </div>
                  <hr />
                  <div className="flex justify-between text-lg font-bold">
                    <span>Total Monthly Revenue</span>
                    <span className="text-green-600">
                      {analyticsLoading ? "..." : formatCurrency(analytics.monthlyRevenue)}
                    </span>
                  </div>
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-600">
                      <strong>Note:</strong> Revenue is calculated as Platform Fee × Number of Active Businesses.
                      Update the platform fee in the section below to change this calculation.
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Status */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold mb-4">Payment Status Overview</h3>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Paid subscriptions</span>
                    <span className="text-green-600 font-semibold">
                      N/A
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Overdue payments</span>
                    <span className="text-red-600 font-semibold">
                      N/A
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Collection rate</span>
                    <span className="text-blue-600 font-semibold">94.2%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Platform Fee Management */}
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold">Platform Fee Management</h3>
                <p className="text-gray-600">Set the monthly subscription fee that all businesses pay to use the platform</p>
              </div>
              <div className="p-6">
                <div className="max-w-md">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Monthly Platform Fee (ZAR)
                      </label>
                      <div className="flex items-center space-x-3">
                        <input
                          type="number"
                          min={0}
                          step={1}
                          className="block w-40 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                          value={monthlyFees['platform'] ?? 499}
                          onChange={e => setMonthlyFees(prev => ({ ...prev, platform: e.target.value }))}
                          disabled={feeSaving['platform']}
                          placeholder="499"
                        />
                        <button
                          className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          onClick={async () => {
                            const platformFee = Number(monthlyFees['platform'] ?? 499);
                            setFeeSaving(prev => ({ ...prev, platform: true }));
                            setFeeErrors(prev => ({ ...prev, platform: null }));
                            setFeeSuccess(prev => ({ ...prev, platform: false }));
                            
                            try {
                              // Update all businesses with the new platform fee
                              const update: BusinessesUpdate = { monthly_fee: platformFee };
                              const { error } = await (supabase.from("businesses") as any)
                                .update(update)
                                .eq("business_status", "active");
                              
                              setFeeSaving(prev => ({ ...prev, platform: false }));
                              
                              if (!error) {
                                setFeeSuccess(prev => ({ ...prev, platform: true }));
                                // Update analytics
                                setAnalytics(prev => ({
                                  ...prev,
                                  monthlyRevenue: platformFee * prev.totalBusinesses
                                }));
                                // Clear success message after 3 seconds
                                setTimeout(() => {
                                  setFeeSuccess(prev => ({ ...prev, platform: false }));
                                }, 3000);
                              } else {
                                setFeeErrors(prev => ({ ...prev, platform: error.message }));
                              }
                            } catch (err) {
                              setFeeSaving(prev => ({ ...prev, platform: false }));
                              setFeeErrors(prev => ({ ...prev, platform: 'Failed to update platform fee' }));
                            }
                          }}
                          disabled={feeSaving['platform'] || monthlyFees['platform'] === "" || isNaN(Number(monthlyFees['platform'] ?? 499))}
                        >
                          {feeSaving['platform'] ? "Updating..." : "Update Platform Fee"}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        This fee will be applied to all {analytics.totalBusinesses} verified businesses
                      </p>
                      {feeSuccess['platform'] && (
                        <p className="text-green-600 text-sm mt-2">✓ Platform fee updated successfully!</p>
                      )}
                      {feeErrors['platform'] && (
                        <p className="text-red-600 text-sm mt-2">{feeErrors['platform']}</p>
                      )}
                    </div>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">Revenue Calculation</h4>
                      <div className="text-sm text-blue-800 space-y-1">
                        <div className="flex justify-between">
                          <span>Platform Fee:</span>
                          <span className="font-medium">{formatCurrency(Number(monthlyFees['platform'] ?? 499))}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Active Businesses:</span>
                          <span className="font-medium">{analytics.totalBusinesses}</span>
                        </div>
                        <hr className="border-blue-200" />
                        <div className="flex justify-between font-medium">
                          <span>Total Monthly Revenue:</span>
                          <span>{formatCurrency(Number(monthlyFees['platform'] ?? 499) * analytics.totalBusinesses)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Chat Support Tab */}
        {activeTab === "chat" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold">WhatsApp Bot Support Dashboard</h3>
                <p className="text-gray-600">Monitor and manage the WhatsApp bot integration</p>
              </div>
              <div className="p-6">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Bot Status */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-medium text-green-900">Bot Status</h4>
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    </div>
                    <p className="text-sm text-green-700 mb-3">WhatsApp Bot is online and operational</p>
                    <a 
                      href="https://tyrelink.co.za/admin" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm font-medium text-green-600 hover:text-green-800"
                    >
                      View Bot Dashboard
                      <svg className="w-4 h-4 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    </a>
                  </div>

                  {/* Active Conversations */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-3">Active Conversations</h4>
                    <div className="text-2xl font-bold text-blue-600 mb-1">24</div>
                    <p className="text-sm text-blue-700">Users currently chatting</p>
                  </div>

                  {/* Today's Messages */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h4 className="font-medium text-purple-900 mb-3">Today's Messages</h4>
                    <div className="text-2xl font-bold text-purple-600 mb-1">156</div>
                    <p className="text-sm text-purple-700">Messages processed today</p>
                  </div>

                  {/* New Registrations */}
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h4 className="font-medium text-orange-900 mb-3">WhatsApp Registrations</h4>
                    <div className="text-2xl font-bold text-orange-600 mb-1">8</div>
                    <p className="text-sm text-orange-700">New users registered today</p>
                  </div>

                  {/* Referral Stats */}
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <h4 className="font-medium text-indigo-900 mb-3">Referral Program</h4>
                    <div className="text-2xl font-bold text-indigo-600 mb-1">42</div>
                    <p className="text-sm text-indigo-700">Active referral codes</p>
                  </div>

                  {/* AI Response Rate */}
                  <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                    <h4 className="font-medium text-teal-900 mb-3">AI Response Rate</h4>
                    <div className="text-2xl font-bold text-teal-600 mb-1">98%</div>
                    <p className="text-sm text-teal-700">Successful AI responses</p>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="mt-8">
                  <h4 className="font-medium text-gray-900 mb-4">Quick Actions</h4>
                  <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <a
                      href="https://tyrelink.co.za/admin"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
                      </svg>
                      Bot Admin
                    </a>
                    <button
                      onClick={() => window.open('/chat', '_blank')}
                      className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      Test Chat
                    </button>
                    <button
                      onClick={() => window.open('https://wa.me/27686247270?text=Admin%20test%20message', '_blank')}
                      className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.787"/>
                      </svg>
                      Test WhatsApp
                    </button>
                    <button
                      className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      onClick={() => {
                        // Placeholder for future analytics export
                        alert('Chat analytics export feature coming soon!')
                      }}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Export Data
                    </button>
                  </div>
                </div>

                {/* Integration Status */}
                <div className="mt-8 bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-4">Integration Status</h4>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">WhatsApp Business API</span>
                      <span className="flex items-center text-green-600">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        Connected
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">OpenAI API</span>
                      <span className="flex items-center text-green-600">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        Operational
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Supabase Database</span>
                      <span className="flex items-center text-green-600">
                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                        Synced
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Reviews Tab */}
        {activeTab === "users" && (
          <UsersRegistrationsTab />
        )}

        {activeTab === "reviews" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <ReviewManagementPage />
          </motion.div>
        )}

        {/* Tyre Fitment Center Suggestions Tab */}
        {activeTab === "suggestions" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-lg shadow-sm">
              <div className="px-6 py-4 border-b">
                <h3 className="text-lg font-semibold">Tyre Fitment Center Suggestions ({filteredSuggestions.length} suggestions)</h3>
                <p className="text-gray-600">Review and manage user-submitted fitment center suggestions</p>
              </div>

              {/* Search and Filter Controls */}
              <div className="p-6 border-b bg-gray-50">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  {/* Search */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                    <input
                      type="text"
                      placeholder="Search name, city, address..."
                      value={suggestionsSearchTerm}
                      onChange={(e) => setSuggestionsSearchTerm(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  {/* Status Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      value={suggestionsStatusFilter}
                      onChange={(e) => setSuggestionsStatusFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="all">All Statuses</option>
                      {uniqueStatuses.filter(status => status !== null).map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>

                  {/* Province Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Province</label>
                    <select
                      value={suggestionsProvinceFilter}
                      onChange={(e) => setSuggestionsProvinceFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Provinces</option>
                      {uniqueProvinces.map(province => (
                        <option key={province} value={province}>{province}</option>
                      ))}
                    </select>
                  </div>

                  {/* Items per page */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Show</label>
                    <select
                      value={suggestionsItemsPerPage}
                      onChange={(e) => setSuggestionsItemsPerPage(Number(e.target.value))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  {/* Sort Controls */}
                  <div className="flex items-center space-x-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Sort by</label>
                      <select
                        value={suggestionsSortBy}
                        onChange={(e) => setSuggestionsSortBy(e.target.value as any)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="created_at">Date Created</option>
                        <option value="name">Name</option>
                        <option value="city">City</option>
                        <option value="status">Status</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                      <select
                        value={suggestionsSortOrder}
                        onChange={(e) => setSuggestionsSortOrder(e.target.value as any)}
                        className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="desc">Newest First</option>
                        <option value="asc">Oldest First</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Business Details</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {suggestionsLoading && (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-gray-500">Loading suggestions...</td>
                      </tr>
                    )}
                    {suggestionsError && (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-red-500">{suggestionsError}</td>
                      </tr>
                    )}
                    {!suggestionsLoading && !suggestionsError && filteredSuggestions.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-gray-500">No suggestions found matching current filters.</td>
                      </tr>
                    )}
                    {!suggestionsLoading && !suggestionsError && paginatedSuggestions.map((suggestion) => (
                      <tr key={suggestion.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{suggestion.name}</div>
                            <div className="text-sm text-gray-500">{suggestion.address}</div>
                            {suggestion.additional_info && (
                              <div className="text-xs text-gray-400 mt-1 max-w-xs truncate" title={suggestion.additional_info}>
                                {suggestion.additional_info}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{suggestion.city}</div>
                          <div className="text-sm text-gray-500">{suggestion.province}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{suggestion.phone || 'Not provided'}</div>
                          <div className="text-sm text-gray-500">{suggestion.email || 'Not provided'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            (suggestion.status === 'submitted' || !suggestion.status || suggestion.status === 'pending') ? 'bg-blue-100 text-blue-800' :
                            suggestion.status === 'read' ? 'bg-green-100 text-green-800' :
                            suggestion.status === 'archived' ? 'bg-gray-100 text-gray-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {(suggestion.status === 'submitted' || !suggestion.status || suggestion.status === 'pending') ? 'Submitted' :
                             suggestion.status === 'read' ? 'Read' :
                             suggestion.status === 'archived' ? 'Archived' :
                             suggestion.status.charAt(0).toUpperCase() + suggestion.status.slice(1)}
                          </span>
                          {suggestion.review_notes && (
                            <div className="text-xs text-gray-500 mt-1 max-w-xs truncate" title={suggestion.review_notes}>
                              {suggestion.review_notes}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {suggestion.created_at ? formatDate(suggestion.created_at) : 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <button
                            onClick={() => {
                              setSelectedSuggestion(suggestion);
                              setSuggestionModalOpen(true);
                            }}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {suggestionsTotalPages > 1 && (
                <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Showing {suggestionsStartIndex + 1} to {Math.min(suggestionsStartIndex + suggestionsItemsPerPage, filteredSuggestions.length)} of {filteredSuggestions.length} suggestions
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setSuggestionsCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={suggestionsCurrentPage === 1}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="px-3 py-2 text-sm">
                      Page {suggestionsCurrentPage} of {suggestionsTotalPages}
                    </span>
                    <button
                      onClick={() => setSuggestionsCurrentPage(prev => Math.min(suggestionsTotalPages, prev + 1))}
                      disabled={suggestionsCurrentPage === suggestionsTotalPages}
                      className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Suggestions Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-sm font-medium text-gray-500">Total Suggestions</h3>
                <p className="text-3xl font-bold text-blue-600">
                  {suggestions.length}
                </p>
                <p className="text-sm text-gray-600 mt-1">All time</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-sm font-medium text-gray-500">Submitted</h3>
                <p className="text-3xl font-bold text-blue-600">
                  {suggestions.filter(s => s.status === 'submitted' || (!s.status || s.status === 'pending')).length}
                </p>
                <p className="text-sm text-gray-600 mt-1">New submissions</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-sm font-medium text-gray-500">Read</h3>
                <p className="text-3xl font-bold text-green-600">
                  {suggestions.filter(s => s.status === 'read').length}
                </p>
                <p className="text-sm text-gray-600 mt-1">Reviewed suggestions</p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-sm">
                <h3 className="text-sm font-medium text-gray-500">Archived</h3>
                <p className="text-3xl font-bold text-gray-600">
                  {suggestions.filter(s => s.status === 'archived').length}
                </p>
                <p className="text-sm text-gray-600 mt-1">Archived suggestions</p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PlatformAdminDashboard;
